/**
 * Wave CM1 — staff side of a live portal call (voice, optional video).
 *
 * setup → ringing → connected → wrap-up. The client must be signed in to the
 * portal to hear it ring. Recording is optional, needs the firm setting, and
 * only starts after the client agrees when answering; the file is uploaded to
 * the matter as a confidential document the portal never shows.
 * Standing React rule: sub-components live at module scope.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CallEnd,
  FiberManualRecord,
  Mic,
  MicOff,
  Phone,
  Videocam,
  VideocamOff,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getAuthToken } from '../../lib/authToken';
import { getApiErrorMessage, toUserErrorMessage } from '../../utils/apiError';
import {
  CallRecorder,
  formatClock,
  getCallMedia,
  mediaErrorMessage,
  pickRecordingMime,
  stopStream,
  waitForIceGathering,
  webrtcSupported,
} from '../../lib/webrtcCall';

type Phase = 'setup' | 'starting' | 'ringing' | 'connecting' | 'connected' | 'ended';

interface CallConfig {
  portalCalls: boolean;
  callRecording: boolean;
  iceServers: RTCIceServer[];
  relay: boolean;
  maxRecordingPartBytes: number;
}

interface CallRow {
  id: string;
  status: string;
  videoEnabled: boolean;
  recordRequested: boolean;
  recordingConsent: 'accepted' | 'declined' | null;
  connectedAt: string | null;
  durationSeconds: number;
  answerSdp?: string | null;
  endReason?: string | null;
  endedBy?: string | null;
}

const authHeaders = (json = true): Record<string, string> => ({
  Authorization: `Bearer ${getAuthToken()}`,
  ...(json ? { 'Content-Type': 'application/json' } : {}),
});

async function api<T = any>(url: string, init?: RequestInit, fallback = 'Request failed'): Promise<T> {
  const res = await fetch(url, { ...init, headers: { ...authHeaders(Boolean(init?.body)), ...(init?.headers || {}) } });
  if (!res.ok) throw new Error(await getApiErrorMessage(res, fallback));
  const json = await res.json();
  return json.data as T;
}

const END_MESSAGES: Record<string, string> = {
  declined: 'The client declined the call.',
  missed: 'No answer — the client did not pick up.',
  cancelled: 'Call cancelled.',
  failed: 'The call could not connect. The client may be on a network that blocks calls.',
  ended: 'Call ended.',
};

interface CallControlsProps {
  muted: boolean;
  cameraOff: boolean;
  hasVideo: boolean;
  onMute: () => void;
  onCamera: () => void;
  onHangUp: () => void;
}

const CallControls: React.FC<CallControlsProps> = ({ muted, cameraOff, hasVideo, onMute, onCamera, onHangUp }) => (
  <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 2 }}>
    <Tooltip title={muted ? 'Unmute' : 'Mute'}>
      <IconButton onClick={onMute} color={muted ? 'warning' : 'default'} sx={{ border: 1, borderColor: 'divider' }} aria-label={muted ? 'Unmute' : 'Mute'}>
        {muted ? <MicOff /> : <Mic />}
      </IconButton>
    </Tooltip>
    {hasVideo && (
      <Tooltip title={cameraOff ? 'Turn camera on' : 'Turn camera off'}>
        <IconButton onClick={onCamera} color={cameraOff ? 'warning' : 'default'} sx={{ border: 1, borderColor: 'divider' }} aria-label="Camera">
          {cameraOff ? <VideocamOff /> : <Videocam />}
        </IconButton>
      </Tooltip>
    )}
    <Tooltip title="Hang up">
      <IconButton onClick={onHangUp} sx={{ bgcolor: 'error.main', color: 'common.white', '&:hover': { bgcolor: 'error.dark' } }} aria-label="Hang up">
        <CallEnd />
      </IconButton>
    </Tooltip>
  </Stack>
);

export interface LiveCallDialogProps {
  open: boolean;
  onClose: () => void;
  clientId: string;
  caseId?: string | null;
  clientName?: string;
}

const LiveCallDialog: React.FC<LiveCallDialogProps> = ({ open, onClose, clientId, caseId = null, clientName }) => {
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<Phase>('setup');
  const [video, setVideo] = useState(false);
  const [record, setRecord] = useState(false);
  const [call, setCall] = useState<CallRow | null>(null);
  const [endMessage, setEndMessage] = useState('');
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [localVideo, setLocalVideo] = useState(false);
  const [remoteHasVideo, setRemoteHasVideo] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [recording, setRecording] = useState(false);
  const [uploadState, setUploadState] = useState<'idle' | 'uploading' | 'done' | 'error'>('idle');
  const [notes, setNotes] = useState('');
  const [bill, setBill] = useState(true);
  const [saving, setSaving] = useState(false);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localRef = useRef<MediaStream | null>(null);
  const remoteRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<CallRecorder | null>(null);
  const callIdRef = useRef<string | null>(null);
  const phaseRef = useRef<Phase>('setup');
  const connectedAtRef = useRef<number | null>(null);
  const remoteVideoEl = useRef<HTMLVideoElement | null>(null);
  const localVideoEl = useRef<HTMLVideoElement | null>(null);

  const setPhaseBoth = (p: Phase) => {
    phaseRef.current = p;
    setPhase(p);
  };

  const { data: config, isLoading: configLoading, error: configError } = useQuery({
    queryKey: ['live-calls', 'config'],
    enabled: open,
    staleTime: 5 * 60 * 1000,
    queryFn: () => api<CallConfig>('/api/live-calls/config', undefined, 'Unable to load call settings'),
  });

  const { data: presence } = useQuery({
    queryKey: ['live-calls', 'presence', clientId],
    enabled: open && phase === 'setup' && Boolean(config?.portalCalls),
    refetchInterval: 10000,
    queryFn: () => api<{ hasPortalLogin: boolean; online: boolean; busy: boolean }>(
      `/api/live-calls/presence?clientId=${encodeURIComponent(clientId)}`, undefined, 'Unable to check the portal',
    ),
  });

  const attachRemote = () => {
    const el = remoteVideoEl.current;
    if (el && remoteRef.current && el.srcObject !== remoteRef.current) {
      el.srcObject = remoteRef.current;
      el.play().catch(() => undefined);
    }
  };

  const teardownMedia = useCallback(() => {
    pcRef.current?.getSenders().forEach((s) => s.track?.stop());
    pcRef.current?.close();
    pcRef.current = null;
    stopStream(localRef.current);
    localRef.current = null;
    remoteRef.current = null;
  }, []);

  const finishRecording = useCallback(async () => {
    const rec = recorderRef.current;
    recorderRef.current = null;
    if (!rec) return;
    setRecording(false);
    setUploadState('uploading');
    let failed = false;
    try {
      await rec.stop();
    } catch {
      failed = true;
    }
    setUploadState((s) => (failed || s === 'error' ? 'error' : 'done'));
  }, []);

  const toEnded = useCallback(async (row: CallRow | null, message?: string) => {
    if (phaseRef.current === 'ended') return;
    setPhaseBoth('ended');
    if (row) setCall(row);
    setEndMessage(message || END_MESSAGES[row?.status || 'ended'] || 'Call ended.');
    await finishRecording();
    teardownMedia();
    queryClient.invalidateQueries({ queryKey: ['calls'] });
    if (caseId) {
      queryClient.invalidateQueries({ queryKey: ['case-activities', caseId] });
      queryClient.invalidateQueries({ queryKey: ['case-timeline', caseId] });
    }
  }, [caseId, finishRecording, queryClient, teardownMedia]);

  const startRecording = useCallback(() => {
    if (recorderRef.current || !localRef.current || !remoteRef.current || !callIdRef.current) return;
    const mime = pickRecordingMime();
    if (!mime) {
      toast.error('This browser cannot record calls. The call continues without a recording.');
      return;
    }
    const id = callIdRef.current;
    const partBytes = Math.min(8 * 1024 * 1024, Math.max(1024 * 1024, (config?.maxRecordingPartBytes || 9 * 1024 * 1024) - 512 * 1024));
    const rec = new CallRecorder(
      async (blob, part) => {
        const res = await fetch(`/api/live-calls/${id}/recording?part=${part}`, {
          method: 'POST',
          headers: { ...authHeaders(false), 'Content-Type': blob.type || 'audio/webm' },
          body: blob,
        });
        if (!res.ok) throw new Error(await getApiErrorMessage(res, 'Recording upload failed'));
      },
      partBytes,
      mime,
      (message) => {
        setUploadState('error');
        toast.error(message);
      },
    );
    rec.addStream(localRef.current);
    rec.addStream(remoteRef.current);
    recorderRef.current = rec;
    rec.start().then(() => setRecording(true)).catch(() => toast.error('Recording could not start.'));
  }, [config?.maxRecordingPartBytes]);

  // Poll the call while ringing / connected.
  useEffect(() => {
    if (!open || !call?.id || !['ringing', 'connecting', 'connected'].includes(phase)) return undefined;
    let stopped = false;
    const tick = async () => {
      try {
        const row = await api<CallRow>(`/api/live-calls/${call.id}`);
        if (stopped) return;
        if (['declined', 'missed', 'cancelled', 'failed', 'ended'].includes(row.status)) {
          const byClient = row.status === 'ended' && row.endedBy === 'client';
          await toEnded(row, byClient ? 'The client hung up.' : undefined);
          return;
        }
        if (row.status === 'connected' && phaseRef.current === 'ringing' && row.answerSdp && pcRef.current) {
          setCall(row);
          setPhaseBoth('connecting');
          await pcRef.current.setRemoteDescription({ type: 'answer', sdp: row.answerSdp });
        }
      } catch {
        /* transient — try again on the next tick */
      }
    };
    const timer = setInterval(tick, phase === 'ringing' ? 1500 : 4000);
    tick();
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [open, call?.id, phase, toEnded]);

  // Give ICE 25s to connect after the answer arrives.
  useEffect(() => {
    if (phase !== 'connecting' || !call?.id) return undefined;
    const id = call.id;
    const t = setTimeout(async () => {
      if (phaseRef.current !== 'connecting') return;
      const row = await api<CallRow>(`/api/live-calls/${id}/fail`, { method: 'POST', body: JSON.stringify({ reason: 'ice_timeout' }) }).catch(() => null);
      await toEnded(row, END_MESSAGES.failed);
    }, 25000);
    return () => clearTimeout(t);
  }, [phase, call?.id, toEnded]);

  // Call clock
  useEffect(() => {
    if (phase !== 'connected') return undefined;
    const t = setInterval(() => {
      if (connectedAtRef.current) setElapsed((Date.now() - connectedAtRef.current) / 1000);
    }, 1000);
    return () => clearInterval(t);
  }, [phase]);

  // Keep media elements bound after re-renders.
  useEffect(() => {
    attachRemote();
    if (localVideoEl.current && localRef.current && localVideoEl.current.srcObject !== localRef.current) {
      localVideoEl.current.srcObject = localRef.current;
    }
  });

  // Leaving the page mid-call hangs up so the client is not left on a dead line.
  useEffect(() => {
    const onUnload = () => {
      const id = callIdRef.current;
      if (id && ['ringing', 'connecting', 'connected'].includes(phaseRef.current)) {
        fetch(`/api/live-calls/${id}/end`, { method: 'POST', headers: authHeaders(), keepalive: true, body: '{}' }).catch(() => undefined);
      }
    };
    window.addEventListener('pagehide', onUnload);
    return () => {
      window.removeEventListener('pagehide', onUnload);
      onUnload();
      recorderRef.current?.stop().catch(() => undefined);
      recorderRef.current = null;
      teardownMedia();
    };
  }, [teardownMedia]);

  const startCall = async () => {
    if (!config) return;
    if (!webrtcSupported()) {
      toast.error('This browser does not support calls. Use a current Chrome, Edge, Firefox or Safari.');
      return;
    }
    setPhaseBoth('starting');
    setEndMessage('');
    setUploadState('idle');
    try {
      let media;
      try {
        media = await getCallMedia(video);
      } catch (err) {
        throw new Error(mediaErrorMessage(err));
      }
      if (video && !media.video) toast('Camera unavailable — calling with voice only.', { icon: 'ℹ️' });
      localRef.current = media.stream;
      setLocalVideo(media.video);

      const pc = new RTCPeerConnection({ iceServers: config.iceServers });
      pcRef.current = pc;
      media.stream.getTracks().forEach((t) => pc.addTrack(t, media.stream));
      // Always offer to receive video on a video call, even if our own camera failed.
      if (video && !media.video) pc.addTransceiver('video', { direction: 'recvonly' });

      remoteRef.current = new MediaStream();
      pc.ontrack = (e) => {
        remoteRef.current?.addTrack(e.track);
        if (e.track.kind === 'video') setRemoteHasVideo(true);
        attachRemote();
      };
      pc.onconnectionstatechange = async () => {
        const st = pc.connectionState;
        if (st === 'connected' && phaseRef.current === 'connecting') {
          connectedAtRef.current = Date.now();
          setPhaseBoth('connected');
          const row = await api<CallRow>(`/api/live-calls/${callIdRef.current}`).catch(() => null);
          if (row) setCall(row);
          if (row?.recordRequested && row.recordingConsent === 'accepted') startRecording();
          if (row?.recordRequested && row.recordingConsent === 'declined') {
            toast('The client declined recording. This call is not being recorded.', { icon: 'ℹ️' });
          }
        }
        if (st === 'failed' && ['connecting', 'connected'].includes(phaseRef.current)) {
          const id = callIdRef.current;
          const wasConnected = phaseRef.current === 'connected';
          const row = id
            ? await api<CallRow>(`/api/live-calls/${id}/${wasConnected ? 'end' : 'fail'}`, {
              method: 'POST', body: JSON.stringify({ reason: 'ice_failed' }),
            }).catch(() => null)
            : null;
          await toEnded(row, wasConnected ? 'The connection dropped.' : END_MESSAGES.failed);
        }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await waitForIceGathering(pc);
      const sdp = pc.localDescription?.sdp;
      if (!sdp) throw new Error('Could not prepare the call.');

      const created = await api<CallRow & { clientOnline: boolean; recordingDropped: boolean }>('/api/live-calls', {
        method: 'POST',
        body: JSON.stringify({ clientId, caseId: caseId || undefined, video, record: record && Boolean(config.callRecording), offerSdp: sdp }),
      }, 'Could not start the call');
      callIdRef.current = created.id;
      setCall(created);
      setPhaseBoth('ringing');
      if (!created.clientOnline) {
        toast('The client does not appear to be in the portal right now. It will ring for 45 seconds if they open it.', { icon: 'ℹ️', duration: 6000 });
      }
    } catch (err) {
      teardownMedia();
      setPhaseBoth('setup');
      toast.error(toUserErrorMessage(err, 'Could not start the call'));
    }
  };

  const hangUp = async () => {
    const id = callIdRef.current;
    const row = id ? await api<CallRow>(`/api/live-calls/${id}/end`, { method: 'POST', body: '{}' }).catch(() => null) : null;
    await toEnded(row, row?.status === 'cancelled' ? END_MESSAGES.cancelled : 'Call ended.');
  };

  const toggleMute = () => {
    const next = !muted;
    localRef.current?.getAudioTracks().forEach((t) => { t.enabled = !next; });
    setMuted(next);
  };

  const toggleCamera = () => {
    const next = !cameraOff;
    localRef.current?.getVideoTracks().forEach((t) => { t.enabled = !next; });
    setCameraOff(next);
  };

  const saveWrapUp = async () => {
    if (!call?.id) return onClose();
    setSaving(true);
    try {
      const canBill = Boolean(caseId) && call.status === 'ended' && (call.durationSeconds || 0) > 0;
      await api(`/api/live-calls/${call.id}/wrapup`, {
        method: 'POST',
        body: JSON.stringify({ notes, createTimeEntry: canBill && bill }),
      }, 'Could not save the call notes');
      toast.success(canBill && bill ? 'Call saved with a billable time entry' : 'Call saved');
      queryClient.invalidateQueries({ queryKey: ['calls'] });
      if (caseId) {
        queryClient.invalidateQueries({ queryKey: ['case-activities', caseId] });
        queryClient.invalidateQueries({ queryKey: ['case-timeline', caseId] });
      }
      onClose();
    } catch (err) {
      toast.error(toUserErrorMessage(err, 'Could not save the call notes'));
    } finally {
      setSaving(false);
    }
  };

  const inCall = ['starting', 'ringing', 'connecting', 'connected'].includes(phase);
  const showVideo = (call?.videoEnabled || video) && (remoteHasVideo || localVideo);
  const who = clientName || 'the client';
  const canBill = Boolean(caseId) && call?.status === 'ended' && (call?.durationSeconds || 0) > 0;

  return (
    <Dialog open={open} onClose={inCall ? undefined : onClose} fullWidth maxWidth={showVideo && inCall ? 'md' : 'sm'} disableEscapeKeyDown={inCall}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Phone fontSize="small" /> Portal call {clientName ? `— ${clientName}` : ''}
        <Box sx={{ flex: 1 }} />
        {recording && <Chip size="small" color="error" icon={<FiberManualRecord />} label="Recording" />}
        {phase === 'connected' && <Chip size="small" label={formatClock(elapsed)} />}
      </DialogTitle>
      <DialogContent>
        {configLoading && <Box sx={{ textAlign: 'center', p: 3 }}><CircularProgress size={28} /></Box>}
        {configError && <Alert severity="error">{toUserErrorMessage(configError, 'Unable to load call settings')}</Alert>}
        {config && !config.portalCalls && (
          <Alert severity="info">
            Portal calls are turned off for your firm. A firm administrator can turn them on under Security → Edit Policy.
          </Alert>
        )}

        {config?.portalCalls && phase === 'setup' && (
          <Stack spacing={2}>
            {presence && !presence.hasPortalLogin && (
              <Alert severity="warning">{who} has no active client portal login yet. Invite them to the portal first.</Alert>
            )}
            {presence?.busy && <Alert severity="warning">{who} is already on a portal call.</Alert>}
            {presence?.hasPortalLogin && !presence.busy && (
              <Alert severity={presence.online ? 'success' : 'info'}>
                {presence.online
                  ? `${who} is in the portal now — the call will ring on their screen.`
                  : `${who} is not in the portal right now. The call rings for 45 seconds, only while they have the portal open. You can also send a portal message asking them to sign in.`}
              </Alert>
            )}
            <FormControlLabel control={<Switch checked={video} onChange={(e) => setVideo(e.target.checked)} />} label="Video call (camera on)" />
            {config.callRecording ? (
              <Box>
                <FormControlLabel control={<Switch checked={record} onChange={(e) => setRecord(e.target.checked)} />} label="Ask the client to allow recording" />
                <Typography variant="body2" color="text.secondary" sx={{ pl: 6 }}>
                  The client chooses when answering. If they say no, the call goes ahead without a recording. Recordings are saved
                  to {caseId ? 'this matter' : 'the client file'} as confidential, privileged documents and are never shown in the portal.
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">Call recording is off for your firm.</Typography>
            )}
            {!config.relay && (
              <Alert severity="warning" variant="outlined">
                No call relay (TURN) is configured on the server, so some office and mobile networks will not connect.
              </Alert>
            )}
          </Stack>
        )}

        {inCall && (
          <Box>
            <Box sx={{ position: 'relative', bgcolor: showVideo ? 'common.black' : 'transparent', borderRadius: 1, overflow: 'hidden', minHeight: showVideo ? 320 : 0 }}>
              <video
                ref={remoteVideoEl}
                autoPlay
                playsInline
                style={{ width: '100%', maxHeight: 480, display: showVideo && remoteHasVideo ? 'block' : 'none' }}
              />
              {localVideo && (
                <video
                  ref={localVideoEl}
                  autoPlay
                  playsInline
                  muted
                  style={{
                    position: showVideo && remoteHasVideo ? 'absolute' : 'static',
                    right: 12,
                    bottom: 12,
                    width: showVideo && remoteHasVideo ? 160 : '100%',
                    maxHeight: 320,
                    borderRadius: 6,
                    border: '2px solid rgba(255,255,255,0.6)',
                    transform: 'scaleX(-1)',
                  }}
                />
              )}
            </Box>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              {phase === 'starting' && <Typography>Preparing the call…</Typography>}
              {phase === 'ringing' && (
                <Stack alignItems="center" spacing={1}>
                  <CircularProgress size={24} />
                  <Typography>Ringing {who} in the portal…</Typography>
                  {call?.recordRequested && <Typography variant="body2" color="text.secondary">They will be asked whether this call may be recorded.</Typography>}
                </Stack>
              )}
              {phase === 'connecting' && <Typography>Connecting…</Typography>}
              {phase === 'connected' && !showVideo && <Typography variant="h6">On call with {who}</Typography>}
              {phase === 'connected' && call?.recordRequested && call.recordingConsent === 'declined' && (
                <Typography variant="body2" color="text.secondary">The client declined recording.</Typography>
              )}
            </Box>
            {phase !== 'starting' && (
              <CallControls
                muted={muted}
                cameraOff={cameraOff}
                hasVideo={localVideo}
                onMute={toggleMute}
                onCamera={toggleCamera}
                onHangUp={hangUp}
              />
            )}
          </Box>
        )}

        {phase === 'ended' && (
          <Stack spacing={2}>
            <Alert severity={call?.status === 'ended' ? 'success' : 'info'}>
              {endMessage}
              {call?.status === 'ended' && call.durationSeconds > 0 ? ` Duration ${formatClock(call.durationSeconds)}.` : ''}
            </Alert>
            {uploadState === 'uploading' && <Alert severity="info" icon={<CircularProgress size={16} />}>Saving the recording to the file… keep this window open.</Alert>}
            {uploadState === 'done' && <Alert severity="success">Recording saved to {caseId ? 'the matter (Call Recordings folder)' : 'the client file'} as a confidential document.</Alert>}
            {uploadState === 'error' && <Alert severity="error">Part of the recording could not be saved.</Alert>}
            {call?.status === 'ended' && (
              <>
                <TextField
                  label="Call notes (added to the call log and activity log)"
                  multiline
                  minRows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  inputProps={{ maxLength: 5000 }}
                />
                {canBill && (
                  <FormControlLabel
                    control={<Checkbox checked={bill} onChange={(e) => setBill(e.target.checked)} />}
                    label={`Create billable time entry (${formatClock(call?.durationSeconds || 0)})`}
                  />
                )}
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {phase === 'setup' && (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              startIcon={video ? <Videocam /> : <Phone />}
              disabled={!config?.portalCalls || !presence?.hasPortalLogin || presence?.busy}
              onClick={startCall}
            >
              Call
            </Button>
          </>
        )}
        {phase === 'ended' && (
          <>
            <Button onClick={onClose} disabled={uploadState === 'uploading'}>Close</Button>
            {call?.status === 'ended' && (
              <Button variant="contained" onClick={saveWrapUp} disabled={saving || uploadState === 'uploading'}>
                {saving ? 'Saving…' : 'Save notes'}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LiveCallDialog;
